import { useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, Settings, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const ROLE_STYLE = { owner: "bg-amber-100 text-amber-800", family: "bg-emerald-100 text-emerald-800", guest: "bg-zinc-200 text-zinc-600" };

/* Top-bar account chip: name + role, dropdown with Settings, Administration (owners) and Log out. */
export const UserMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const role = user.role || "guest";
  const initial = (user.name || user.email || "?").trim()[0]?.toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid="user-menu-trigger" title={user.email}
          className="flex h-8 shrink-0 items-center gap-2 rounded-full border border-zinc-200 bg-white pl-1 pr-2.5 text-sm text-zinc-700 transition-colors hover:border-zinc-900 hover:text-zinc-900 data-[state=open]:border-zinc-900">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-900 font-heading text-[11px] font-bold text-white" aria-hidden>{initial}</span>
          <span className="hidden max-w-[9rem] truncate sm:inline" data-testid="user-menu-name">{user.name || user.email}</span>
          <span className={`rounded px-1.5 py-0.5 font-mono text-[9px] uppercase ${ROLE_STYLE[role] || ROLE_STYLE.guest}`} data-testid="user-menu-role">{role}</span>
          <ChevronDown size={12} className="text-zinc-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-56" data-testid="user-menu">
        <DropdownMenuLabel className="font-normal">
          <p className="truncate text-sm font-medium" data-testid="user-menu-full-name">{user.name}</p>
          <p className="truncate font-mono text-[11px] text-zinc-500" data-testid="user-menu-email">{user.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate("/settings")} data-testid="user-menu-settings" className="gap-2"><Settings size={14} />Settings</DropdownMenuItem>
        {role === "owner" && <DropdownMenuItem onSelect={() => navigate("/settings?page=accounts")} data-testid="user-menu-admin" className="gap-2"><ShieldCheck size={14} />Administration</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={logout} data-testid="logout-button" className="gap-2 text-red-700 focus:text-red-700"><LogOut size={14} />Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
